/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'react-query';
import { API } from 'api';
import { approvalBulkUploadTypesData } from 'views/EmployerManagement/shared/Settings&ConfigForm/data';
import { ISharedEmployerInfoOrEmployerRequestInfoDataOutput } from '../shared';

interface IEmployerRequestInfoResponse {
  data: {
    id: string;
    createdBy: string;
    createdOn: number;
    status: string;
    type: string;
    fieldUpdated: string[];
    reason: string;
    updatedBy?: {
      aid: string;
      name: string;
      email: string;
      role: string;
      createdOn: number;
      status: string;
    }[];
    data: {
      eid: string;
      name?: string;
      companyName: string;
      companyLogo: string;
      companyInformation: {
        bnmCategory: string;
        bnmSubCategory: string;
        businessLicenseExpiryDate: string;
        // SUNSET FEATURE 2026-01-08: remarksForm - commented out as part of feature sunset
        // companyDescription: string;
        companyRegistrationNo: {
          new: string;
          old: string;
        };
        employerType: string;
        // SUNSET FEATURE 2026-01-08: GST Registration No
        // gstRegistrationNo: string;
        owner: {
          name: string;
          ic: string;
        }[];
        inAppCompanyLogo: boolean;
      };
      contactInformation: {
        email: string;
        mailing: {
          addressLine1: string;
          addressLine2: string;
          city: string;
          contactPerson: string;
          country: string;
          postalCode: string;
          state: string;
        };
        mobile: string;
        secondaryEmail: string;
        website: string;
        // SUNSET FEATURE 2026-01-08: Work Phone
        // workPhone: string;
      };
      attachments: {
        companyLogo: string;
        // SUNSET FEATURE 2026-01-08: HLB Application Form
        // hlbApplicationForm: {
        //   file: string;
        //   name: string;
        // };
        // SUNSET FEATURE 2026-01-08: Audited Financial Statements
        // auditedFinancialStatements: {
        //   file: string;
        //   name: string;
        // };
        businessRegistrationDocument: {
          file: string;
          name: string;
        };
        // SUNSET FEATURE 2026-01-08: Letter of Authorisation
        // letterOfAuthorisation: {
        //   file: string;
        //   name: string;
        // };
        signedAgreement: {
          file: string;
          name: string;
        };
        ctosReport: {
          file: string;
          name: string;
        };
        others: {
          file: string;
          name: string;
        };
      };
      finance: {
        attentionTo: string;
        // SUNSET FEATURE 2026-01-08: Bank Account Holder Name, Bank Account No, Bank Name
        // bankAccountHolderName: string;
        // bankAccountNo: string;
        // bankName: string;
        masterAccountNo: string;
        masterBankName: string;
        masterAccountHolderName: string;
        masterSwiftCode: string;
        paymentDueDate: string;
        // SUNSET FEATURE - Obsolete: cifNo
        // cifNo: string;
      };
      withdrawal: {
        ltv?: number;
        dailyCreditLtv?: number;
        monthlyCreditLtv?: number;
        monthlyWithdrawalLtv?: number;
        monthlyLimit?: number;
        limit: {
          min: number;
          max: number;
        };
        withdrawalProhibitedDayForNewlyBindedUser: number;
        withdrawalProhibitedDay: number;
      };
      locations: {
        createdBy: string;
        createdOn: number;
        id: string;
        location: {
          lat: number;
          long: number;
        };
        name: string;
        qr: {
          qrImage: string;
          qrImageTemplate: string;
        };
      }[];
      admin: {
        aid: string;
        loginId: string;
        role: string;
        actId: string;
        status: string;
      }[];
      // SUNSET FEATURE 2026-01-08: checkInMethod - commented out as part of feature sunset
      // checkInMethod: string[];
      scheduler?: boolean;
      earning?: string;
      status: string;
      createdOn: number;
      setting: {
        isFeeAbsorbed: boolean;
        isKickStartEarningEnabled: boolean;
        kickStartEarningAmount?: number;
        withdrawalCycleType: 'standard' | 'custom' | undefined;
        globalWithdrawalCycleId?: string;
        type?: string;
        actType?: string;
        // SUNSET FEATURE 2026-01-08: Employee Movement File
        // employeeMovementFile: string;
        lastWithdrawalDate?: number;
        // SUNSET FEATURE 2026-01-08: Employee Movement File Generation Days
        // employeeMovementFileGenerationDays: string[];
        commissionRequiresAuthentication: boolean;
        // SUNSET FEATURE 2026-01-08: useOba - commented out as part of feature sunset
        // useOba: boolean;
        customTemplate: {
          onboard: {
            file: string;
            name: string;
            fileList: { name: string; status: string; url: string }[];
            source: string;
          };
          onboardAgent: {
            file: string;
            name: string;
            fileList: { name: string; status: string; url: string }[];
            source: string;
          };
          update: {
            file: string;
            name: string;
            fileList: { name: string; status: string; url: string }[];
            source: string;
          };
        };
        // SUNSET FEATURE - Obsolete: deductionFileGenerationDay
        // deductionFileGenerationDay: any[];
        // SUNSET FEATURE 2026-01-08: requiresDeductionFile - commented out as part of feature sunset
        // requiresDeductionFile: boolean | null;

        // SUNSET FEATURE - Obsolete
        // deductionInvoiceGenerationDate?: any[];
        invoiceFileRecipient: {
          to: string;
          cc: string;
        };

        // SUNSET FEATURE 2026-01-08: Employee Movement File Mailing List
        // employeeMovementFileRecipient: {
        //   to: string;
        //   cc: string;
        // };

        deductionFileRecipient: {
          to: string;
          cc: string;
        };
        withdrawalCycle: {
          tag: string;
          dateFrom: number;
          dateTo: number;
        }[];
        bulkImportApprovalFlag: boolean;
        bulkImportApprovalTypes?: Array<string>;
        customGrantingTimeDayEnabled?: boolean;
        customGrantingTime?: string;
        customGrantingDay?: string;
        creditBasedAccrualEnabled?: boolean;
        customWithdrawalSettings: {
          customWithdrawalSettingsEnabled: boolean;
          txnMinWithdrawal: number;
          txnMaxWithdrawal: number;
          dailyMaxWithdrawal: number;
          monthlyMaxWithdrawal: number;
          withdrawalFrequencyPerMonth?: number | null;
        };
        deductionFileType: string | null | undefined;
        whiteLabel?: string;
        corporateCode?: string;
      };
    };
  };
}

const getEmployerRequestInfo = (eid: string) => {
  return API.get()(`/api/admin/employerRequest/${eid}`);
};

export const useEmployerRequestInfoData = (eid: string) => {
  return useQuery(['employerRequest', eid], (): any => getEmployerRequestInfo(eid), {
    select: (response: IEmployerRequestInfoResponse) => {
      const outputObj = response.data;
      const output = outputObj.data;
      const res: ISharedEmployerInfoOrEmployerRequestInfoDataOutput = {
        ...output,
        type: outputObj.type,
        id: outputObj.id,
        companyInformation: {
          status: outputObj.status,
          name: output.name,
          createdOn: outputObj.createdOn,
          companyName: output.companyName,
          website: output.contactInformation?.website,
          reason: outputObj.reason,
          ...output.companyInformation,
        },
        contactInformation: {
          ...output.contactInformation,
        },
        additionalInformation: {
          // SUNSET FEATURE 2026-01-08: Bank Name, Bank Account No, Bank Account Holder Name
          // bankName: output.finance?.bankName,
          // bankAccountNo: output.finance?.bankAccountNo,
          // bankAccountHolderName: output.finance?.bankAccountHolderName,
          masterAccountNo: output.finance?.masterAccountNo,
          masterBankName: output.finance?.masterBankName,
          masterAccountHolderName: output.finance?.masterAccountHolderName,
          masterSwiftCode: output.finance?.masterSwiftCode,
          paymentDueDate: output.finance?.paymentDueDate,
          attentionTo: output.finance?.attentionTo,
          // SUNSET FEATURE - Obsolete: cifNo
          // cifNo: output.finance.cifNo,
          // SUNSET FEATURE 2026-01-08: GST Registration No, Work Phone
          // gstRegistrationNo: output.companyInformation?.gstRegistrationNo,
          // workPhone: output.contactInformation?.workPhone,
          mobile: output.contactInformation?.mobile,
          email: output.contactInformation?.email,
          secondaryEmail: output.contactInformation?.secondaryEmail,
          mailingContactPerson: output.contactInformation?.mailing?.contactPerson,
          mailingAddressLine1: output.contactInformation?.mailing?.addressLine1,
          mailingAddressLine2: output.contactInformation?.mailing?.addressLine2,
          mailingCity: output.contactInformation?.mailing?.city,
          mailingState: output.contactInformation?.mailing?.state,
          mailingPostalCode: output.contactInformation?.mailing?.postalCode,
          mailingCountry: output.contactInformation?.mailing?.country,
          // SUNSET FEATURE 2026-01-08: useOba - commented out as part of feature sunset
          // useOba: output.setting?.useOba,
        },
        attachments: {
          ...output.attachments,
          owners: output.companyInformation?.owner,
        },
        setting: {
          ...output.setting,
          customTemplate: {
            onboard: output.setting.customTemplate?.onboard,
            onboardAgent: output.setting.customTemplate?.onboardAgent,
            update: output.setting.customTemplate?.update,
          },
          earning: output.earning,
          ltv: output.withdrawal?.ltv,
          dailyCreditLtv: output.withdrawal?.dailyCreditLtv,
          monthlyCreditLtv: output.withdrawal?.monthlyCreditLtv,
          monthlyWithdrawalLtv: output.withdrawal?.monthlyWithdrawalLtv,
          monthlyLimit: output.withdrawal?.monthlyLimit,
          // SUNSET FEATURE 2026-01-08: checkInMethod - commented out as part of feature sunset
          // checkInMethod: output.checkInMethod,
          // useCheckInOutFlag: output.checkInMethod && output.checkInMethod.length.toString(),
          scheduler: output.scheduler,
          // SUNSET FEATURE 2026-01-08: useCheckInOutFlag - commented out as part of feature sunset
          // useCheckInOutFlag: output.checkInMethod && output.checkInMethod.length > 0 ? '1' : '0',
          admin: output.admin,
          cycles: output.setting.withdrawalCycle,
          customWithdrawalSettingsEnabled: output.setting.customWithdrawalSettings?.customWithdrawalSettingsEnabled,
          txnMaxWithdrawal: output.setting.customWithdrawalSettings?.txnMaxWithdrawal,
          txnMinWithdrawal: output.setting.customWithdrawalSettings?.txnMinWithdrawal,
          dailyMaxWithdrawal: output.setting.customWithdrawalSettings?.dailyMaxWithdrawal,
          monthlyMaxWithdrawal: output.setting.customWithdrawalSettings?.monthlyMaxWithdrawal,
          withdrawalFrequencyPerMonth: output.setting.customWithdrawalSettings?.withdrawalFrequencyPerMonth,
          bulkImportApprovalFlag: output.setting?.bulkImportApprovalFlag ?? true,
          bulkImportApprovalTypes:
            (output.setting?.bulkImportApprovalFlag ?? true) && output.setting.bulkImportApprovalTypes === undefined
              ? approvalBulkUploadTypesData.map((item) => item.value)
              : output.setting.bulkImportApprovalTypes ?? [],
          whiteLabel: output.setting?.whiteLabel,
          corporateCode: output.setting?.corporateCode,
        },
        // SUNSET FEATURE 2026-01-08: remarksForm - commented out as part of feature sunset
        // remarks: {
        //   companyDescription: output.companyInformation?.companyDescription,
        // },
        fieldUpdated: outputObj.fieldUpdated,
        updatedBy: outputObj.updatedBy,
      };

      return res;
    },
  });
};
